import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import { submitQueueable } from '../../offline-queue';
import type { CorrectiveCreateBody, WorkOrderRecord, WorkloadRecord } from './work-orders.types';

const BASE = '/v1/work-orders';
const KEY = ['work-orders'] as const;

export function useWorkOrders(openOnly = true) {
  return useQuery({
    queryKey: [...KEY, { openOnly }],
    queryFn: () => api.get<WorkOrderRecord[]>(`${BASE}?open_only=${openOnly}`),
    refetchInterval: 30_000,
  });
}

export function useWorkOrder(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => api.get<WorkOrderRecord>(`${BASE}/${id}`),
    enabled: !!id,
  });
}

/** QR scan entry point (R-129): open WOs for a scanned asset tag. */
export function useWorkOrdersByTag(tag: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'by-tag', tag],
    queryFn: () => api.get<WorkOrderRecord[]>(`${BASE}/by-tag/${encodeURIComponent(tag ?? '')}`),
    enabled: !!tag,
  });
}

export function useWorkload() {
  return useQuery({
    queryKey: [...KEY, 'workload'],
    queryFn: () => api.get<WorkloadRecord[]>(`${BASE}/workload`),
    refetchInterval: 60_000,
  });
}

export function useCreateCorrectiveWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CorrectiveCreateBody) => api.post<WorkOrderRecord>(BASE, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Event-based PM catalog tasks (frequency_days = 0) for an asset (R-133). */
export interface EventTask {
  id: string;
  activity: string;
  pm_type: string;
  est_duration_min: number | null;
  skill_level: string | null;
}
export function useEventTasks(assetId: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'event-tasks', assetId],
    queryFn: () => api.get<EventTask[]>(`${BASE}/event-tasks/${assetId}`),
    enabled: !!assetId,
    staleTime: 300_000,
  });
}

/** Raise one event-based PM task as a work order. */
export function useRaiseEventPm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ asset_id, catalog_id }: { asset_id: string; catalog_id: string }) =>
      api.post<WorkOrderRecord>(`${BASE}/raise-event`, { asset_id, catalog_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAssignWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, group_id, assignee_id }: { id: string; group_id?: string; assignee_id?: string }) =>
      api.post<WorkOrderRecord>(`${BASE}/${id}/assign`, { group_id, assignee_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useStartWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<WorkOrderRecord>(`${BASE}/${id}/start`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Complete a work order. Offline-queued (R-145): the scan-to-complete flow at
 *  the machine may be offline, so a network failure queues the completion and
 *  it flushes on reconnect. A replay of an already-completed WO 409s and is
 *  dropped as already-applied. */
export function useCompleteWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, source, note }: { id: string; source?: 'ui' | 'qr_scan'; note?: string }) =>
      submitQueueable<WorkOrderRecord>({
        method: 'POST',
        path: `${BASE}/${id}/complete`,
        body: { source, note },
        invalidate: [...KEY],
        label: `Complete WO ${id.slice(0, 8)}`,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCancelWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api.post<WorkOrderRecord>(`${BASE}/${id}/cancel`, { note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Cloud HQ read of the work-order fleet mirror (read-only per D-106). */
export function useFleetWorkOrders(openOnly = true) {
  return useQuery({
    queryKey: [...KEY, 'fleet', { openOnly }],
    queryFn: () =>
      api.get<{ site_id: string; row: WorkOrderRecord; occurred_at_ms: number }[]>(
        `/users/v1/itsm/fleet/work-orders?open_only=${openOnly}`,
      ),
    refetchInterval: 30_000,
  });
}
